import React, { FC, useCallback } from 'react';
import classNames from 'classnames';
import { AxiosPromise } from 'axios';

import {
  GetPaymentsSummaryByTermNumberResponse,
  PaymentsAndBillingInfoType,
  GetRenewalPayPlanOptionsResponse,
} from '@clearcover/core-app-types';
import { FormInput, LoadingPage, useToast } from 'Shared/components';
import { FlexGrid, FlexGridColumn } from '@clearcover/atlas';
import { env } from 'Shared/constants/env';
import { useLazyApi } from 'Shared/services/Api';
import { useNewApi } from 'Shared/services/Api/makeApiHttp';
import { usePolicyParams } from 'Policies/utils';
import styles from './styles.module.css';

export type PaymentFrequencySectionProps = {
  billingInfo?: PaymentsAndBillingInfoType;
  isRenewalDraft: boolean;
  fetchBillingData: () =>
    | AxiosPromise<GetPaymentsSummaryByTermNumberResponse>
    | Promise<void>;
  billingInfoLoading: boolean;
  mandatedDownPay: boolean;
};

export const PaymentFrequencySection: FC<PaymentFrequencySectionProps> = ({
  billingInfo,
  isRenewalDraft,
  fetchBillingData,
  billingInfoLoading,
  mandatedDownPay,
}) => {
  const { toast } = useToast();
  const { policyId } = usePolicyParams();

  const [{ loading: updatingRenewalPayPlan }, updateRenewalPayPlan] =
    useLazyApi({
      url: 'policies/renewal/updatePayPlan',
      method: 'POST',
    });

  const [{ data: payPlanOptions, loading: payPlanOptionsLoading }] =
    useNewApi<GetRenewalPayPlanOptionsResponse>({
      url: `policies/renewal/${policyId}/payPlanOptions`,
    });

  const updatePayPlan = useCallback(
    (paymentPlanEnum: string) => {
      updateRenewalPayPlan({
        data: {
          policyId,
          paymentPlanEnum,
        },
      })
        .then(() => {
          fetchBillingData().then(() => {
            toast({
              withSecondaryNav: false,
              message: 'Payment Frequency has been updated',
            });
          });
        })
        .catch((error) => {
          if (error.response.data.metadata.response.statusCode === 200) {
            fetchBillingData().then(() => {
              toast({
                withSecondaryNav: false,
                message: 'Payment Frequency has been updated',
              });
            });
          } else toast({ message: error.message, type: 'error' });
        });
    },
    [updateRenewalPayPlan, policyId, fetchBillingData, toast]
  );

  const { payPlanEnum } = billingInfo || {};

  const availablePlans = payPlanOptions?.renewalPayPlans || [];

  const filteredPlans = mandatedDownPay
    ? availablePlans.filter((plan) => plan.planEnum.includes('full_pay'))
    : availablePlans;

  const content = payPlanOptionsLoading ? (
    <LoadingPage />
  ) : (
    <>
      <h5>Payment Frequency</h5>
      {filteredPlans.length > 0 &&
        filteredPlans.map((plan, index) => {
          const isSelected = plan.planEnum === payPlanEnum;
          const isFirst = index === 0;
          const isLast = index === filteredPlans.length - 1;

          return (
            <FormInput
              key={plan.planEnum}
              className={classNames(styles.costRadioButton, {
                [styles.checked]: isSelected,
                [styles.top]: isFirst,
                [styles.bottom]: isLast,
              })}
              variant={'radio'}
              label={
                <div className={styles.costRadioButtonLabel}>
                  <h5 className={styles.radioLabel}>{plan.planName}</h5>
                  <div className={styles.radioSubLabel}>{plan.description}</div>
                  <div className={styles.radioButtonCost}></div>
                </div>
              }
              disabled={
                !isRenewalDraft ||
                updatingRenewalPayPlan ||
                billingInfoLoading ||
                payPlanOptionsLoading
              }
              checked={isSelected}
              onChecked={() => {
                updatePayPlan(plan.planEnum);
              }}
              name={'payment-frequency'}
              value={plan.planEnum}
            />
          );
        })}
    </>
  );

  if (env.paymentsAndBillingNewDesign) {
    // When displayed in modal, use FlexGrid layout like EditBillingInfo
    return (
      <FlexGrid className={styles.container}>
        <FlexGridColumn mobile={8} mobileOffset={2}>
          <section className={styles.paymentFrequency}>{content}</section>
        </FlexGridColumn>
      </FlexGrid>
    );
  }

  // When displayed inline, use simple section layout
  return <section className={styles.paymentFrequency}>{content}</section>;
};
